/**
 * Build the ordered list of DOM nodes to move into each .lay-text-line wrapper.
 *
 * Splitting.js keeps .word spans inside <a> etc.; the old logic moved every .word
 * node and detached link text. We move the nearest inline wrapper instead when all
 * .word nodes inside it belong to the same visual line.
 *
 * Splitting also inserts plain whitespace as Text nodes (not .whitespace spans) for
 * some boundaries (e.g. around <strong>). Those nodes were not moved into
 * .lay-text-line and spaces disappeared — we wrap them as .whitespace first.
 */

const INLINE_WRAP_ROOT_SELECTOR =
	'a, button, strong, em, b, i, u, mark, abbr, cite, code, kbd, samp, var, sub, sup, dfn, small, span.laybutton'

const TEXT_BLOCK_TAGS = new Set(['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'])

/**
 * Return the text blocks owned by one text-animation root in DOM order.
 *
 * Lay Theme wraps the whole text in an anchor when the element itself has a
 * link (`.lay-text-animation > a > p`). Those paragraphs must be split just
 * like direct children; otherwise the animation never initializes and the
 * CSS keeps the text hidden. Keep this deliberately shallow so nested text
 * from buttons or other inline content is not mistaken for another block.
 */
export function getTextAnimationBlockElements(target) {
	if (!target || !target.children) {
		return []
	}

	const blocks = []
	Array.from(target.children).forEach((child) => {
		if (TEXT_BLOCK_TAGS.has(child.tagName)) {
			blocks.push(child)
			return
		}
		if (child.tagName === 'A') {
			Array.from(child.children).forEach((linkedChild) => {
				if (TEXT_BLOCK_TAGS.has(linkedChild.tagName)) {
					blocks.push(linkedChild)
				}
			})
		}
	})
	return blocks
}

function isWordOnLine(word, lineStr) {
	return word.classList.contains('word') && word.style.getPropertyValue('--line-index') === lineStr
}

function findPrevWordInBlock(el) {
	let n = el.previousElementSibling
	while (n) {
		if (n.classList && n.classList.contains('word')) {
			return n
		}
		const inner = n.querySelectorAll && n.querySelectorAll('.word')
		if (inner && inner.length) {
			return inner[inner.length - 1]
		}
		n = n.previousElementSibling
	}
	return null
}

function findNextWordInBlock(el) {
	let n = el.nextElementSibling
	while (n) {
		if (n.classList && n.classList.contains('word')) {
			return n
		}
		const inner = n.querySelector && n.querySelector('.word')
		if (inner) {
			return inner
		}
		n = n.nextElementSibling
	}
	return null
}

function getInlineWrapRoot(word, paragraph, lineStr) {
	const hit = word.closest(INLINE_WRAP_ROOT_SELECTOR)
	if (!hit || hit === paragraph || !paragraph.contains(hit)) {
		return null
	}
	const innerWords = hit.querySelectorAll('.word')
	if (!innerWords.length) {
		return null
	}
	for (let i = 0; i < innerWords.length; i++) {
		if (innerWords[i].style.getPropertyValue('--line-index') !== lineStr) {
			return null
		}
	}
	return hit
}

function normalizeWhitespaceForVisibleSpan(text) {
	return /[\r\n\t\f\v]/.test(text) ? ' ' : text
}

/**
 * @param {HTMLElement} paragraph
 */
export function normalizeWhitespaceOnlyTextNodes(paragraph) {
	const walker = document.createTreeWalker(paragraph, NodeFilter.SHOW_TEXT, null)
	const toReplace = []
	let n
	while ((n = walker.nextNode())) {
		const t = n.textContent
		if (t.length > 0 && /^\s+$/.test(t)) {
			toReplace.push(n)
		}
	}
	for (let i = 0; i < toReplace.length; i++) {
		const textNode = toReplace[i]
		const span = document.createElement('span')
		span.className = 'whitespace'
		span.appendChild(document.createTextNode(normalizeWhitespaceForVisibleSpan(textNode.textContent)))
		textNode.parentNode.replaceChild(span, textNode)
	}
}

/**
 * @param {HTMLElement} paragraph
 * @param {number} lineIndex
 * @returns {HTMLElement[]}
 */
export function getLineElementsForWrap(paragraph, lineIndex) {
	const lineStr = String(lineIndex)
	const candidates = Array.from(paragraph.querySelectorAll('.word, .whitespace'))
	const out = []
	const seenRoots = new WeakSet()

	for (let i = 0; i < candidates.length; i++) {
		const el = candidates[i]

		if (el.classList.contains('whitespace')) {
			const prevW = findPrevWordInBlock(el)
			const nextW = findNextWordInBlock(el)
			if (!prevW || !nextW) {
				continue
			}
			if (!isWordOnLine(prevW, lineStr) || !isWordOnLine(nextW, lineStr)) {
				continue
			}
			out.push(el)
			continue
		}

		if (!el.classList.contains('word')) {
			continue
		}
		if (!isWordOnLine(el, lineStr)) {
			continue
		}

		const root = getInlineWrapRoot(el, paragraph, lineStr)
		if (root) {
			if (seenRoots.has(root)) {
				continue
			}
			seenRoots.add(root)
			out.push(root)
			continue
		}

		out.push(el)
	}

	return out
}

/**
 * Splitting often omits `.whitespace` where a line wraps — two `.word` nodes become adjacent. Insert `span.whitespace`.
 * Recurses into wrappers (e.g. links) but not into `.word` leaves.
 */
export function ensureWhitespaceBetweenAdjacentWordElements(parent) {
	if (!parent || !parent.childNodes) {
		return
	}
	let i = 0
	while (i < parent.childNodes.length - 1) {
		const a = parent.childNodes[i]
		const b = parent.childNodes[i + 1]
		if (
			a.nodeType === Node.ELEMENT_NODE &&
			b.nodeType === Node.ELEMENT_NODE &&
			a.classList.contains('word') &&
			b.classList.contains('word')
		) {
			const ws = document.createElement('span')
			ws.className = 'whitespace'
			ws.appendChild(document.createTextNode(' '))
			parent.insertBefore(ws, b)
			i += 2
			continue
		}
		i++
	}
	for (let j = 0; j < parent.children.length; j++) {
		const child = parent.children[j]
		if (child.classList.contains('word') || child.classList.contains('whitespace')) {
			continue
		}
		ensureWhitespaceBetweenAdjacentWordElements(child)
	}
}

/**
 * typewriter / typewriter_fade: keep one .lay-text-line per block so inline .char/.word reflow like normal text.
 * Clears stale data-pretext-split (Pretext no longer used) and merges multiple .lay-text-line groups from Splitting.
 */
export function consolidateTypewriterParagraphIntoSingleLine(paragraph) {
	if (!paragraph) {
		return
	}
	paragraph.removeAttribute('data-pretext-split')
	const wrapper = document.createElement('div')
	wrapper.className = 'lay-text-line'
	while (paragraph.firstChild) {
		const node = paragraph.firstChild
		if (node.nodeType === Node.ELEMENT_NODE && node.classList && node.classList.contains('lay-text-line')) {
			while (node.firstChild) {
				wrapper.appendChild(node.firstChild)
			}
			node.remove()
		} else {
			wrapper.appendChild(node)
		}
	}
	paragraph.appendChild(wrapper)
	normalizeWhitespaceOnlyTextNodes(wrapper)
	ensureWhitespaceBetweenAdjacentWordElements(wrapper)
}
